<template>
  <BDrawerMenu
    v-model="isOpen"
    class="trigger-action-form-container"
    width="600px"
    :title="$t('trigger.actionSetting.title')"
    @cancel="handleCancelClick"
  >
    <div class="trigger-action-form-body-container">
      <BListItem class="pb-100">
        <span class="fs-300 fw-bold">{{ $t('trigger.actionSetting.targetSelect') }}</span>
      </BListItem>
      <BListItem class="list-item">
        <template #header>
          <span>{{ $t('trigger.actionSetting.actionSelect') }}</span>
        </template>
        <BSelect
          v-model="internalTriggerAction.actionType"
          :items="groupedActionTypes"
          item-text="name"
          item-value="value"
          fit
          required
        />
      </BListItem>
      <BListItem class="list-item">
        <div
          v-if="!!internalTriggerAction.actionType && forms.length > 0"
          v-loading="loading"
          class="action-detail-container form"
        >
          <div
            v-for="form in forms"
            :key="form.actionDetailKey"
          >
            <template v-if="form.inputType === 'sequence_select'">
              <BListItem class="list-item">
                <template #header>
                  <span>{{ form.label }}</span>
                </template>
                <SequenceMasterSimpleSelect v-model="internalTriggerAction.actionDetail[form.actionDetailKey]" />
              </BListItem>
            </template>
            <template v-if="form.inputType === 'sequence_owner_select'">
              <BListItem class="list-item">
                <template #header>
                  <span>{{ form.label }}</span>
                </template>
                <SelectSequenceOwner v-model="internalTriggerAction.actionDetail[form.actionDetailKey]" />
              </BListItem>
            </template>
            <template v-if="form.inputType === 'lead_stage_select'">
              <BListItem class="list-item">
                <template #header>
                  <span>{{ form.label }}</span>
                </template>
                <LeadStageSimpleSelect
                  v-model="internalTriggerAction.actionDetail[form.actionDetailKey]"
                  required
                />
              </BListItem>
            </template>
          </div>
        </div>
      </BListItem>
      <BListItem class="pt-200 pb-200">
        <span class="fs-300 fw-bold">{{ $t('trigger.actionSetting.approveUserSelect') }}</span>
      </BListItem>
      <BListItem class="list-item">
        <ElRadioGroup v-model="hasApproveUser">
          <ElRadio :value="true">
            {{ $t('trigger.actionSetting.setApproveUser') }}
          </ElRadio>
          <ElRadio :value="false">
            {{ $t('trigger.actionSetting.noApproveUser') }}
          </ElRadio>
        </ElRadioGroup>
      </BListItem>
      <BListItem
        v-if="hasApproveUser"
        class="list-item"
      >
        <template #header>
          <span>{{ $t('trigger.actionSetting.approveUser') }}</span>
        </template>
        <SelectUser
          v-model="internalTriggerAction.approveUserId"
          :use-all="false"
          :clearable="false"
        />
      </BListItem>
    </div>
    <template #footer>
      <div class="footer">
        <BLayout
          align-center
          justify-center
        >
          <BBtn
            class="mr-300 btn"
            text
            @click="handleCancelClick"
          >
            {{ $t('general.cancel.text') }}
          </BBtn>
          <BBtn
            class="ml-300 btn"
            type="primary"
            outline
            :disabled="!isFormValid"
            @click="handleSetClick"
          >
            {{ $t('general.setting.text') }}
          </BBtn>
        </BLayout>
      </div>
    </template>
  </BDrawerMenu>
</template>

<script lang="ts" setup>
import { ref, watch, nextTick, computed } from 'vue';
import SelectSequenceOwner, { TSequenceOwnerValue } from '@/components/organisms/user/trigger/triggerActionForm/SelectSequenceOwner.vue';
import { useTriggerAction, useTriggerActionForm } from '@/composable/user/trigger/trigger-action';
import LeadStageSimpleSelect from '../LeadStageSimpleSelect.vue';
import SequenceMasterSimpleSelect from '../SequenceMasterSimpleSelect.vue';
import { TriggerActionBodyExt } from '../types';

type TEmits = {
  'click:cancel': [];
};
const emit = defineEmits<TEmits>();

const model = defineModel<TriggerActionBodyExt | null>();
const isOpen = defineModel<boolean>('isOpen', { default: false });

const { groupedActionTypes, makeInitialTriggerAction } = useTriggerAction();
const { forms, fetchForms, loading } = useTriggerActionForm();
const internalTriggerAction = ref<TriggerActionBodyExt>();
const hasApproveUser = ref<boolean>(true);

const clearInternalValues = () => {
  internalTriggerAction.value = makeInitialTriggerAction(model.value);
  forms.value = [];
  // NOTE: 初期値は承認者を設定する(true)
  hasApproveUser.value = model.value == null
    ? true
    : model.value.approveUserId != null;
};
clearInternalValues();

watch(() => internalTriggerAction.value.actionType, (value, oldValue) => {
  if (oldValue) {
    internalTriggerAction.value.actionDetail = {};
    forms.value = [];
  }
  if (value) fetchForms(value);
});

watch(() => isOpen.value, () => {
  nextTick(() => {
    clearInternalValues();
  });
});

watch(() => hasApproveUser.value, (val) => {
  if (!val) {
    internalTriggerAction.value.approveUserId = null;
  }
});

const isFormValid = computed(() => {
  const actionTypeValid = !!internalTriggerAction.value.actionType;
  const actionDetailValid = forms.value.length === 0 || forms.value.every(form => {
    return !!internalTriggerAction.value.actionDetail[form.actionDetailKey];
  });
  const approveUserValid = !hasApproveUser.value || internalTriggerAction.value.approveUserId != null;

  return actionTypeValid && actionDetailValid && approveUserValid;
});

const handleCancelClick = () => {
  emit('click:cancel');
};

const handleSetClick = () => {
  model.value = internalTriggerAction.value;
};
</script>
